import {
  ScrollableCurriculumTabStrip,
  type ScrollableCurriculumTabStripHandle,
} from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { CurriculumParentStripNavSyncContext } from "@/contexts/CurriculumParentStripNavSyncContext";
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BeltRank } from "../../MembersArea";
import Jitae from "./Jitae";
import Keumgang from "./Keumgang";
import Koryo from "./Koryo";
import PyongWon from "./PyongWon";
import SipJin from "./SipJin";
import Taebaek from "./Taebaek";

type Section = { id: string; label: string };

const DEFAULT_SURFACE = "!bg-gray-200 !text-gray-900";

export interface CurriculumKukkiwonProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  beltTabSurfaceClass: string;
  hasDemo: (aliases: string[]) => boolean;
  onWatchDemo: (aliases: string[]) => void;
  showKoryo: boolean;
  showKeumgang: boolean;
  showTaebaek: boolean;
  showPyongWon: boolean;
  showSipJin: boolean;
  showJitae: boolean;
}

export default function CurriculumKukkiwon({
  selectedBelt,
  getBeltColor,
  beltTabSurfaceClass,
  hasDemo,
  onWatchDemo,
  showKoryo,
  showKeumgang,
  showTaebaek,
  showPyongWon,
  showSipJin,
  showJitae,
}: CurriculumKukkiwonProps) {
  const sections: Section[] = useMemo(() => {
    const rows: Section[] = [];
    if (showKoryo) rows.push({ id: "koryo", label: "Koryo" });
    if (showKeumgang) rows.push({ id: "keumgang", label: "Keumgang" });
    if (showTaebaek) rows.push({ id: "taebaek", label: "Taebaek" });
    if (showPyongWon) rows.push({ id: "pyongwon", label: "PyongWon" });
    if (showSipJin) rows.push({ id: "sipjin", label: "SipJin" });
    if (showJitae) rows.push({ id: "jitae", label: "Jitae" });
    return rows;
  }, [showKoryo, showKeumgang, showTaebaek, showPyongWon, showSipJin, showJitae]);

  const fingerprint = sections.map((s) => s.id).join("|");
  const [mainTab, setMainTab] = useState(() => sections[0]?.id ?? "koryo");

  useEffect(() => {
    if (!sections.some((s) => s.id === mainTab)) {
      setMainTab(sections[0]?.id ?? "");
    }
  }, [fingerprint, mainTab, sections]);

  const surface = beltTabSurfaceClass ?? DEFAULT_SURFACE;
  const kukkiwonStripRef = useRef<ScrollableCurriculumTabStripHandle>(null);
  const scrollKukkiwonOrchestratorStrip = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      kukkiwonStripRef.current?.getScrollContainer() ?? null,
    );
  }, []);

  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium transition-shadow",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  if (sections.length === 0) return null;

  return (
    <div className="space-y-4 text-left">
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full space-y-4">
        <ScrollableCurriculumTabStrip
          ref={kukkiwonStripRef}
          key={fingerprint}
          ariaLabelPrefix="Kukkiwon patterns"
        >
          {sections.map((s) => (
            <TabsTrigger key={s.id} value={s.id} className={triggerClass}>
              {s.label}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>

        <CurriculumParentStripNavSyncContext.Provider value={scrollKukkiwonOrchestratorStrip}>
        {showKoryo && (
          <TabsContent value="koryo" className="mt-0 focus-visible:outline-none">
            <Koryo
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showKeumgang && (
          <TabsContent value="keumgang" className="mt-0 focus-visible:outline-none">
            <Keumgang
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showTaebaek && (
          <TabsContent value="taebaek" className="mt-0 focus-visible:outline-none">
            <Taebaek
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showPyongWon && (
          <TabsContent value="pyongwon" className="mt-0 focus-visible:outline-none">
            <PyongWon
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showSipJin && (
          <TabsContent value="sipjin" className="mt-0 focus-visible:outline-none">
            <SipJin
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showJitae && (
          <TabsContent value="jitae" className="mt-0 focus-visible:outline-none">
            <Jitae
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}
        </CurriculumParentStripNavSyncContext.Provider>
      </Tabs>
    </div>
  );
}
